import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CatalogNativeSelect from "./CatalogNativeSelect";
import {
  applyProductCodeRegen,
  getProductCodeRegenOptions,
  previewProductCodeRegen,
} from "./services/catalogMappingService";
import {
  buildCodeSourceOptions,
  buildDbColumnOptions,
  buildFeedFieldOptions,
  buildMatchLogicOptions,
  DEFAULT_CODE_SOURCES,
  DEFAULT_DB_CROSSWALK_COLUMNS,
  DEFAULT_MATCH_LOGIC,
  DEFAULT_MATCH_RULE,
  filterPreviewRowsByStatus,
  formatRegenMatchRuleLabel,
  parseProductCodeRegenOptions,
} from "./utils/productCodeRegenUtils";

const PREVIEW_STATUS_FILTERS = {
  unmatched: "unmatched",
  ambiguous: "ambiguous",
  not_generable: "not_generable",
};

const countCards = (preview) => [
  { label: "Cataloghi vendor", value: preview?.total_catalogs ?? 0, filterKey: null },
  { label: "Già OK", value: preview?.counts?.already_ok ?? 0, filterKey: null },
  { label: "Da aggiornare", value: preview?.would_change_code ?? 0, filterKey: null },
  { label: "Non abbinati", value: preview?.counts?.unmatched ?? 0, filterKey: PREVIEW_STATUS_FILTERS.unmatched },
  { label: "Non generabili", value: preview?.counts?.not_generable ?? 0, filterKey: PREVIEW_STATUS_FILTERS.not_generable },
  { label: "Ambigui", value: preview?.counts?.ambiguous ?? 0, filterKey: PREVIEW_STATUS_FILTERS.ambiguous },
  { label: "Attuali URL (legacy)", value: preview?.legacy_url ?? 0, filterKey: null },
  { label: "Attuali AUTO (legacy)", value: preview?.legacy_auto ?? 0, filterKey: null },
];

const ProductCodeRegenPanel = ({
  vendorId,
  fieldKeys = [],
  disabled = false,
  showError,
}) => {
  const [codeSource, setCodeSource] = useState("internal");
  const [matchLogic, setMatchLogic] = useState(DEFAULT_MATCH_LOGIC);
  const [savedMatchRules, setSavedMatchRules] = useState([]);
  const [draftRule, setDraftRule] = useState({ ...DEFAULT_MATCH_RULE });
  const [feedFields, setFeedFields] = useState(fieldKeys);
  const [dbColumns, setDbColumns] = useState(DEFAULT_DB_CROSSWALK_COLUMNS);
  const [codeSources, setCodeSources] = useState(DEFAULT_CODE_SOURCES);
  const [preview, setPreview] = useState(null);
  const [previewStatusFilter, setPreviewStatusFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);

  const applyOptions = useCallback((options) => {
    const parsed = parseProductCodeRegenOptions(options, fieldKeys);
    setFeedFields(parsed.feedFields);
    setDbColumns(parsed.dbColumns);
    setCodeSources(parsed.codeSources);
    if (options?.match_logic) {
      setMatchLogic(options.match_logic);
    }
  }, [fieldKeys]);

  useEffect(() => {
    setFeedFields((current) => (current.length ? current : fieldKeys));
  }, [fieldKeys]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const options = await getProductCodeRegenOptions(vendorId);
        if (active) {
          applyOptions(options);
        }
      } catch (error) {
        if (active) {
          applyOptions({});
          showError?.(error.message || "Errore caricamento opzioni rigenerazione codice prodotto");
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [vendorId, applyOptions, showError]);

  const feedFieldOptions = useMemo(
    () => buildFeedFieldOptions([...(feedFields || []), ...(fieldKeys || [])]),
    [feedFields, fieldKeys],
  );

  const dbColumnOptions = useMemo(
    () => buildDbColumnOptions(dbColumns),
    [dbColumns],
  );

  const codeSourceOptions = useMemo(
    () => buildCodeSourceOptions(codeSources),
    [codeSources],
  );

  const matchLogicOptions = useMemo(
    () => buildMatchLogicOptions(),
    [],
  );

  const updateDraftRule = useCallback((key, value) => {
    setDraftRule((current) => ({ ...current, [key]: value }));
  }, []);

  const addRule = useCallback(() => {
    const feedField = String(draftRule.feed_field || "").trim();
    const dbColumn = String(draftRule.db_column || "").trim();
    if (!feedField || !dbColumn) {
      showError?.("Seleziona campo feed e colonna DB prima di aggiungere l'abbinamento");
      return;
    }
    const nextRule = { feed_field: feedField, db_column: dbColumn };
    if (savedMatchRules.some(
      (rule) => rule.feed_field === nextRule.feed_field && rule.db_column === nextRule.db_column,
    )) {
      showError?.("Abbinamento già presente");
      return;
    }
    setSavedMatchRules((current) => [...current, nextRule]);
    setDraftRule({ ...DEFAULT_MATCH_RULE });
  }, [draftRule, showError, savedMatchRules]);

  const removeRule = useCallback((index) => {
    setSavedMatchRules((current) => current.filter((_, ruleIndex) => ruleIndex !== index));
  }, []);

  const buildPayloadOptions = useCallback(() => ({
    codeSource,
    matchLogic,
    matchRules: savedMatchRules,
  }), [codeSource, matchLogic, savedMatchRules]);

  const handleSimulate = useCallback(async () => {
    if (savedMatchRules.length === 0) {
      showError?.("Aggiungi almeno un abbinamento feed ↔ DB prima di simulare");
      return;
    }
    setLoading(true);
    setConfirmApply(false);
    setPreviewStatusFilter(null);
    try {
      const result = await previewProductCodeRegen(vendorId, buildPayloadOptions());
      setPreview(result);
      applyOptions(result);
      if (result?.error) {
        showError?.(result.error);
      }
    } catch (error) {
      showError?.(error.message || "Errore simulazione rigenerazione");
    } finally {
      setLoading(false);
    }
  }, [vendorId, buildPayloadOptions, applyOptions, showError, savedMatchRules.length]);

  const handleApply = useCallback(async () => {
    if (savedMatchRules.length === 0) {
      showError?.("Aggiungi almeno un abbinamento feed ↔ DB prima di eseguire la rigenerazione");
      return;
    }
    if (!confirmApply) {
      setConfirmApply(true);
      return;
    }
    setApplying(true);
    try {
      const result = await applyProductCodeRegen(vendorId, buildPayloadOptions());
      if (result?.error) {
        showError?.(result.error);
      }
      setPreview(result?.preview || preview);
      setConfirmApply(false);
    } catch (error) {
      showError?.(error.message || "Errore applicazione rigenerazione");
    } finally {
      setApplying(false);
    }
  }, [vendorId, buildPayloadOptions, confirmApply, preview, showError, savedMatchRules.length]);

  const togglePreviewStatusFilter = useCallback((filterKey) => {
    if (!filterKey) {
      return;
    }
    setPreviewStatusFilter((current) => (current === filterKey ? null : filterKey));
  }, []);

  const cards = useMemo(() => countCards(preview), [preview]);
  const previewRows = useMemo(
    () => filterPreviewRowsByStatus(preview?.sample_rows, previewStatusFilter),
    [preview, previewStatusFilter],
  );
  const controlsDisabled = disabled || loading || applying;
  const matchLogicLabel = matchLogic === "or" ? "OR" : "AND";

  return (
    <Paper className="cm-panel">
      <Typography variant="h6" className="cm-spacer-md">
        Rigenera Codice Prodotto
      </Typography>
      <Typography variant="body2" color="text.secondary" className="cm-spacer-md">
        Configura la sorgente del nuovo codice prodotto e una o più relazioni feed ↔ DB
        per il crosswalk. Con più abbinamenti: AND richiede che tutti concordino, OR usa
        il primo abbinamento valido.
      </Typography>

      <div className="cm-regen-toolbar-row cm-spacer-md">
        <CatalogNativeSelect
          className="cm-regen-code-source-select"
          value={codeSource}
          onChange={setCodeSource}
          options={codeSourceOptions}
          placeholder="Sorgente codice"
          ariaLabel="Sorgente codice prodotto"
          disabled={controlsDisabled}
        />
        <CatalogNativeSelect
          className="cm-regen-match-logic-select"
          value={matchLogic}
          onChange={setMatchLogic}
          options={matchLogicOptions}
          placeholder="Logica match"
          ariaLabel="Logica matching abbinamenti multipli"
          disabled={controlsDisabled}
        />
        <div className="cm-regen-toolbar-actions">
          <button
            type="button"
            className="cm-btn cm-btn-outlined"
            onClick={handleSimulate}
            disabled={controlsDisabled}
          >
            {loading ? "Simulazione..." : "Simula rigenerazione"}
          </button>
          <button
            type="button"
            className="cm-btn cm-btn-primary"
            onClick={handleApply}
            disabled={controlsDisabled || !preview || preview?.error || savedMatchRules.length === 0}
          >
            {applying
              ? "Applicazione..."
              : confirmApply
                ? "Conferma rigenerazione"
                : "Esegui rigenerazione"}
          </button>
        </div>
      </div>

      <div className="cm-regen-crosswalk-panel cm-spacer-md">
        <div className="cm-regen-crosswalk-form">
          <span className="cm-regen-crosswalk-heading" aria-hidden="true">
            <span>ABBINAMENTO</span>
            <span>PER UNICITA&apos;</span>
          </span>
          <CatalogNativeSelect
            className="cm-regen-feed-select"
            value={draftRule.feed_field}
            onChange={(value) => updateDraftRule("feed_field", value)}
            options={feedFieldOptions}
            placeholder="Campo feed"
            ariaLabel="Campo feed nuovo abbinamento"
            disabled={controlsDisabled}
          />
          <span className="cm-regen-rule-arrow" aria-hidden="true">→</span>
          <CatalogNativeSelect
            className="cm-regen-db-select"
            value={draftRule.db_column}
            onChange={(value) => updateDraftRule("db_column", value)}
            options={dbColumnOptions}
            placeholder="Colonna DB"
            ariaLabel="Colonna DB nuovo abbinamento"
            disabled={controlsDisabled}
          />
          <button
            type="button"
            className="cm-btn cm-btn-secondary cm-regen-add-btn"
            onClick={addRule}
            disabled={controlsDisabled || !draftRule.feed_field || !draftRule.db_column}
          >
            Aggiungi abbinamento
          </button>
        </div>

        {savedMatchRules.length > 0 && (
          <div className="cm-regen-crosswalk-rules">
            {savedMatchRules.map((rule, index) => (
              <div
                key={`${rule.feed_field}-${rule.db_column}-${index}`}
                className="cm-regen-crosswalk-card"
              >
                {index > 0 && (
                  <span className="cm-regen-crosswalk-logic-badge" aria-hidden="true">
                    {matchLogicLabel}
                  </span>
                )}
                <span className="cm-regen-crosswalk-card-label">
                  {formatRegenMatchRuleLabel(rule, dbColumnOptions)}
                </span>
                <IconButton
                  size="small"
                  className="cm-regen-remove-btn"
                  aria-label={`Rimuovi abbinamento ${index + 1}`}
                  disabled={controlsDisabled}
                  onClick={() => removeRule(index)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
          </div>
        )}
      </div>

      {preview?.match_rules_label && (
        <Typography variant="body2" className="cm-spacer-sm">
          Crosswalk attivo ({matchLogicLabel}): {preview.match_rules_label}
        </Typography>
      )}

      {preview?.import_ready === true && (
        <Alert severity="success" className="cm-spacer-md">
          Vendor pronto per import: nessun unmatched/ambiguous/not_generable.
        </Alert>
      )}
      {preview && preview.import_ready === false && !preview.error && (
        <Alert severity="warning" className="cm-spacer-md">
          Review consigliata prima dell&apos;import: verificare righe unmatched o ambiguous.
        </Alert>
      )}
      {preview?.error && (
        <Alert severity="error" className="cm-spacer-md">{preview.error}</Alert>
      )}

      {preview && !preview.error && (
        <>
          <div className="cm-regen-metrics cm-spacer-md">
            {cards.map((card) => (
              <div key={card.label} className="cm-regen-metric-card">
                <div className="cm-regen-metric-header">
                  <div className="cm-regen-metric-value">{card.value}</div>
                  {card.filterKey ? (
                    <button
                      type="button"
                      className={`cm-regen-metric-eye${previewStatusFilter === card.filterKey ? " is-active" : ""}`}
                      aria-label={`Mostra solo ${card.label.toLowerCase()} in anteprima`}
                      aria-pressed={previewStatusFilter === card.filterKey}
                      onClick={() => togglePreviewStatusFilter(card.filterKey)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </button>
                  ) : null}
                </div>
                <div className="cm-regen-metric-label">{card.label}</div>
              </div>
            ))}
          </div>

          <Typography variant="subtitle2" className="cm-spacer-sm">
            Anteprima righe
            {previewStatusFilter === PREVIEW_STATUS_FILTERS.unmatched && " — solo non abbinati"}
            {previewStatusFilter === PREVIEW_STATUS_FILTERS.not_generable && " — solo non generabili"}
            {previewStatusFilter === PREVIEW_STATUS_FILTERS.ambiguous && " — solo ambigui"}
            {!previewStatusFilter && " (campione)"}
          </Typography>
          <div className="cm-table-wrap">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>catalog_list_id</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Codice attuale</TableCell>
                  <TableCell>Codice proposto</TableCell>
                  <TableCell>Crosswalk</TableCell>
                  <TableCell>Stato</TableCell>
                  <TableCell>Feed row</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRows.map((row) => (
                  <TableRow key={`${row.catalog_list_id}-${row.status}-${row.feed_row || ""}`}>
                    <TableCell>{row.catalog_list_id}</TableCell>
                    <TableCell>{row.nome || "—"}</TableCell>
                    <TableCell>{row.current_code || "—"}</TableCell>
                    <TableCell>{row.proposed_code || "—"}</TableCell>
                    <TableCell>{row.match_key || "—"}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>{row.feed_row ?? "—"}</TableCell>
                  </TableRow>
                ))}
                {previewRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      Nessuna riga da mostrare
                      {previewStatusFilter ? " per il filtro selezionato" : ""}.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </Paper>
  );
};

export default ProductCodeRegenPanel;
